import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Plus, X, Camera } from 'lucide-react';
import { SkeletonList } from '../components/ui/Skeleton';
import { PageShell } from '../components/ui/PageShell';
import { SectionGroup } from '../components/ui/SectionGroup';
import { Timestamp } from 'firebase/firestore';
import { DamageReport, Project, Item, Photo } from '../types';
import { getProject } from '../services/projectService';
import { getItemsByProject } from '../services/itemService';
import { getDamageReport, createDamageReport, updateDamageReport, deleteDamageReport } from '../services/damageReportService';
import { getPhotosByItem, getPhotosByProject, getPhoto } from '../services/photoService';
import { useOnlineStatus } from '../hooks/useOnlineStatus';
import { useFormNavigation } from '../hooks/useFormNavigation';
import UnsavedChangesDialog from '../components/UnsavedChangesDialog';
import ConfirmDialog from '../components/ConfirmDialog';
import CameraComponent from '../components/CameraComponent';
import PhotoViewer from '../components/PhotoViewer';
import { useFullScreen } from '../components/Layout';
import { useNotifications } from '../contexts/NotificationContext';

type DamageReportFormData = {
  description: string;
  reportedBy: string;
  status: 'open' | 'resolved' | 'in-progress';
  resolution?: string;
  itemId?: string;
  photoIds: string[];
};

const DamageReportFormPage: React.FC = () => {
  const { projectId, reportId, itemId: urlItemId } = useParams<{ projectId: string; reportId?: string; itemId?: string }>();
  const navigate = useNavigate();
  const isOnline = useOnlineStatus();
  const isEditMode = !!reportId;
  const { addNotification } = useNotifications();

  const [project, setProject] = useState<Project | null>(null);
  const [items, setItems] = useState<Item[]>([]);
  const [photos, setPhotos] = useState<Photo[]>([]);
  const [availablePhotos, setAvailablePhotos] = useState<Photo[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showCamera, setShowCamera] = useState(false);
  const [selectedPhoto, setSelectedPhoto] = useState<Photo | null>(null);
  const [photoLoadError, setPhotoLoadError] = useState<Record<string, boolean>>({});
  const [formChanged, setFormChanged] = useState(false);
  const [initialFormData, setInitialFormData] = useState<DamageReportFormData | null>(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  const [formData, setFormData] = useState<DamageReportFormData>({
    description: '',
    reportedBy: '',
    status: 'open',
    resolution: '',
    itemId: urlItemId || undefined,
    photoIds: []
  });

  const { setIsFullScreen } = useFullScreen();

  // Hide tab bar when camera is open
  useEffect(() => {
    setIsFullScreen(showCamera);
    return () => setIsFullScreen(false);
  }, [showCamera, setIsFullScreen]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value as any
    }));
  };

  const handleAddPhoto = (photo: Photo) => {
    if (!formData.photoIds.includes(photo.id)) {
      setFormData(prev => ({
        ...prev,
        photoIds: [...prev.photoIds, photo.id]
      }));
      setPhotos(prev => [...prev, photo]);
      setAvailablePhotos(prev => prev.filter(p => p.id !== photo.id));
    }
  };

  const handleRemovePhoto = (photoId: string) => {
    setFormData(prev => ({
      ...prev,
      photoIds: prev.photoIds.filter(id => id !== photoId)
    }));
    const removedPhoto = photos.find(p => p.id === photoId);
    if (removedPhoto) {
      setPhotos(prev => prev.filter(p => p.id !== photoId));
      setAvailablePhotos(prev => [...prev, removedPhoto]);
    }
  };

  const handleSaveAndNavigate = async (): Promise<boolean> => {
    if (!projectId) return false;

    if (!isOnline) {
      setError('Du är offline. Anslut till internet för att spara skaderapporten.');
      return false;
    }

    if (!formData.description.trim()) {
      setError('Beskrivning måste anges.');
      return false;
    }

    if (!formData.reportedBy.trim()) {
      setError('Rapporterad av måste anges.');
      return false;
    }

    try {
      setSaving(true);

      const cleanedFormData = { ...formData };
      if (cleanedFormData.itemId === undefined) {
        delete cleanedFormData.itemId;
      }

      if (isEditMode && reportId) {
        await updateDamageReport(reportId, cleanedFormData);
        setInitialFormData(cleanedFormData);
        setFormChanged(false);
        addNotification({ type: 'success', message: 'Skaderapport har sparats', duration: 2000 });
        navigate(`/projects/${projectId}/damage-reports/${reportId}`);
        return true;
      } else {
        await createDamageReport({
          ...cleanedFormData,
          projectId,
          reportedAt: Timestamp.now()
        });
        addNotification({ type: 'success', message: 'Skaderapport har skapats', duration: 2000 });
        navigate(`/projects/${projectId}/damage-reports`);
        return true;
      }
    } catch (err) {
      console.error('Error saving damage report:', err);
      setError(`Kunde inte spara skaderapporten: ${err instanceof Error ? err.message : 'Okänt fel'}`);
      return false;
    } finally {
      setSaving(false);
    }
  };

  // Form navigation safety
  const {
    navigateSafely,
    showDialog,
    handleSave,
    handleDiscard,
    handleContinueEditing,
  } = useFormNavigation({
    onSave: handleSaveAndNavigate,
    hasUnsavedChanges: formChanged
  });

  useEffect(() => {
    const fetchData = async () => {
      if (!projectId) return;

      try {
        setLoading(true);

        const baseFetches = [
          getProject(projectId),
          getItemsByProject(projectId),
          isEditMode && reportId ? getDamageReport(reportId) : Promise.resolve(null),
        ] as const;

        const [fetchedProject, fetchedItems, fetchedReport] = await Promise.all(baseFetches);

        setProject(fetchedProject);
        setItems(fetchedItems);

        if (isEditMode && fetchedReport) {
          const reportFormData = {
            description: fetchedReport.description,
            reportedBy: fetchedReport.reportedBy,
            status: fetchedReport.status,
            resolution: fetchedReport.resolution || '',
            itemId: fetchedReport.itemId,
            photoIds: fetchedReport.photoIds || []
          };

          setFormData(reportFormData);
          setInitialFormData(reportFormData);

          if (fetchedReport.photoIds && fetchedReport.photoIds.length > 0) {
            const allPhotos = fetchedReport.itemId
              ? await getPhotosByItem(projectId, fetchedReport.itemId)
              : await getPhotosByProject(projectId);

            const reportPhotos = allPhotos.filter(photo =>
              fetchedReport.photoIds.includes(photo.id)
            );
            setPhotos(reportPhotos);

            const otherPhotos = allPhotos.filter(photo =>
              !fetchedReport.photoIds.includes(photo.id)
            );
            setAvailablePhotos(otherPhotos);
          }
        } else {
          const available = urlItemId
            ? await getPhotosByItem(projectId, urlItemId)
            : await getPhotosByProject(projectId);

          setAvailablePhotos(available);
          setInitialFormData(formData);
        }

        setError(null);
      } catch (err) {
        console.error('Error fetching data:', err);
        setError('Kunde inte hämta data. Försök igen senare.');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [projectId, reportId, urlItemId, isEditMode]);

  // Track form changes
  useEffect(() => {
    if (loading || !initialFormData) return;
    const hasChanges = JSON.stringify(formData) !== JSON.stringify(initialFormData);
    setFormChanged(hasChanges);
  }, [formData, initialFormData, loading]);

  const handleSubmit = async (e?: React.FormEvent) => {
    if (e) e.preventDefault();
    await handleSaveAndNavigate();
  };

  const handleDelete = () => {
    if (!reportId || !isOnline) return;
    setShowDeleteConfirm(true);
  };

  const confirmDelete = async () => {
    if (!reportId) return;
    try {
      setSaving(true);
      await deleteDamageReport(reportId);
      setShowDeleteConfirm(false);
      navigate(`/projects/${projectId}/damage-reports`);
    } catch (err) {
      console.error('Error deleting damage report:', err);
      setError('Kunde inte ta bort skaderapporten. Försök igen senare.');
    } finally {
      setSaving(false);
    }
  };

  const handlePhotoTaken = async (photoId: string) => {
    try {
      const newPhoto = await getPhoto(photoId);
      if (newPhoto) {
        handleAddPhoto(newPhoto);
      }
    } catch (err) {
      console.error('Error adding new photo:', err);
    }
  };

  const handleImageError = (photoId: string) => {
    setPhotoLoadError(prev => ({ ...prev, [photoId]: true }));
  };

  const backLink = urlItemId
    ? `/projects/${projectId}/items/${urlItemId}`
    : `/projects/${projectId}/damage-reports`;

  if (showCamera) {
    return (
      <CameraComponent
        projectId={projectId!}
        onPhotoTaken={handlePhotoTaken}
        onClose={() => setShowCamera(false)}
      />
    );
  }

  if (loading) {
    return (
      <PageShell title={isEditMode ? 'Redigera skaderapport' : 'Ny skaderapport'} backTo={backLink} backLabel="Tillbaka">
        <div className="space-y-4 py-2">
          <SkeletonList count={4} />
        </div>
      </PageShell>
    );
  }

  if (!project) {
    return (
      <PageShell title="Skaderapport" backTo="/" backLabel="Tillbaka">
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800/40 text-red-700 dark:text-red-400 px-4 py-3 rounded-xl text-sm mt-4">
          Projektet kunde inte hittas.
        </div>
      </PageShell>
    );
  }

  return (
    <PageShell
      title={isEditMode ? 'Redigera skaderapport' : 'Ny skaderapport'}
      onBack={() => navigateSafely(backLink)}
      backLabel="Tillbaka"
      headerRight={
        <div className="flex items-center gap-4">
          {isEditMode && (
            <button
              type="button"
              onClick={handleDelete}
              className="text-red-500 active:text-red-700 text-[17px] font-normal transition-colors"
            >
              Radera
            </button>
          )}
          <button
            type="button"
            onClick={() => handleSubmit()}
            disabled={saving}
            className={`text-[17px] font-semibold transition-colors ${
              saving
                ? 'text-[var(--text-tertiary)]'
                : 'text-accent-600 dark:text-accent-400 active:opacity-50'
            }`}
          >
            {saving ? 'Sparar…' : 'Spara'}
          </button>
        </div>
      }
    >
      <ConfirmDialog
        isOpen={showDeleteConfirm}
        title="Ta bort"
        message="Är du säker på att du vill ta bort denna skaderapport? Detta kan inte ångras."
        confirmLabel="Ta bort"
        cancelLabel="Avbryt"
        variant="danger"
        loading={saving}
        onConfirm={confirmDelete}
        onCancel={() => setShowDeleteConfirm(false)}
      />

      <UnsavedChangesDialog
        isOpen={showDialog}
        onClose={handleContinueEditing}
        onSave={handleSave}
        onDiscard={handleDiscard}
      />

      {selectedPhoto && (
        <PhotoViewer
          photos={[selectedPhoto]}
          initialPhotoIndex={0}
          onClose={() => setSelectedPhoto(null)}
          onDelete={async (photoId) => {
            handleRemovePhoto(photoId);
            setSelectedPhoto(null);
          }}
        />
      )}

      <form onSubmit={(e) => e.preventDefault()} className="space-y-6 pb-8">
        {error && (
          <div className="bg-red-50 dark:bg-red-900/20 border-l-2 border-red-400 px-3 py-2.5 rounded-ios-card">
            <p className="text-[13px] text-red-700 dark:text-red-400">{error}</p>
          </div>
        )}

        {/* Report Details */}
        <SectionGroup title="Rapportdetaljer">
          <div className="flex items-center gap-3 px-4 py-3 min-h-[44px] inset-divider">
            <label className="text-[15px] text-[var(--text-primary)] w-24 sm:w-28 flex-shrink-0">Föremål</label>
            <div className="flex-1 min-w-0">
              <select
                name="itemId"
                value={formData.itemId || ''}
                onChange={handleInputChange}
                disabled={isEditMode}
                className="bg-transparent text-[15px] text-[var(--text-primary)] w-full outline-none py-0 appearance-none cursor-pointer text-right"
              >
                <option value="">Inget specifikt</option>
                {items.map((item) => (
                  <option key={item.id} value={item.id}>{item.name}</option>
                ))}
              </select>
            </div>
          </div>
          <div className="flex items-center gap-3 px-4 py-3 min-h-[44px] inset-divider">
            <label className="text-[15px] text-[var(--text-primary)] w-24 sm:w-28 flex-shrink-0">
              Rapportör<span className="text-red-500 ml-0.5">*</span>
            </label>
            <div className="flex-1 min-w-0">
              <input
                type="text"
                name="reportedBy"
                value={formData.reportedBy}
                onChange={handleInputChange}
                required
                autoComplete="name"
                enterKeyHint="next"
                placeholder="Ange namn"
                className="bg-transparent text-[15px] text-[var(--text-primary)] w-full placeholder-[var(--text-tertiary)] outline-none py-0 text-right"
              />
            </div>
          </div>
          <div className="flex items-center gap-3 px-4 py-3 min-h-[44px]">
            <label className="text-[15px] text-[var(--text-primary)] w-24 sm:w-28 flex-shrink-0">Status</label>
            <div className="flex-1 min-w-0">
              <select
                name="status"
                value={formData.status}
                onChange={handleInputChange}
                className="bg-transparent text-[15px] text-[var(--text-primary)] w-full outline-none py-0 appearance-none cursor-pointer text-right"
              >
                <option value="open">Öppen</option>
                <option value="in-progress">Pågående</option>
                <option value="resolved">Löst</option>
              </select>
            </div>
          </div>
        </SectionGroup>

        {/* Description */}
        <SectionGroup title="Beskrivning">
          <div className="px-4 py-3">
            <textarea
              name="description"
              value={formData.description}
              onChange={handleInputChange}
              rows={4}
              required
              enterKeyHint="done"
              placeholder="Beskriv skadan..."
              className="w-full bg-transparent text-[15px] text-[var(--text-primary)] placeholder-[var(--text-tertiary)] outline-none resize-none"
            />
          </div>
        </SectionGroup>

        {/* Resolution - only for resolved or in-progress */}
        {(formData.status === 'resolved' || formData.status === 'in-progress') && (
          <SectionGroup title="Lösning">
            <div className="px-4 py-3">
              <textarea
                name="resolution"
                value={formData.resolution || ''}
                onChange={handleInputChange}
                rows={4}
                enterKeyHint="done"
                placeholder="Beskriv lösningen..."
                className="w-full bg-transparent text-[15px] text-[var(--text-primary)] placeholder-[var(--text-tertiary)] outline-none resize-none"
              />
            </div>
          </SectionGroup>
        )}

        {/* Photos */}
        <SectionGroup title="Foton">
          <div className="px-4 py-3 space-y-3">
            <button
              type="button"
              onClick={() => setShowCamera(true)}
              className="flex items-center text-accent-600 text-[15px] active:opacity-50"
            >
              <Camera size={16} className="mr-1" />
              Ta foto
            </button>

            {photos.length > 0 && (
              <div className="grid grid-cols-3 sm:grid-cols-4 gap-2">
                {photos.map((photo) => (
                  <div key={photo.id} className="relative aspect-square rounded-lg overflow-hidden">
                    <img
                      src={photo.thumbnail || photo.url}
                      loading="lazy"
                      alt=""
                      className="w-full h-full object-cover cursor-pointer"
                      onClick={() => setSelectedPhoto(photo)}
                      onError={() => handleImageError(photo.id)}
                    />
                    <button
                      type="button"
                      onClick={() => handleRemovePhoto(photo.id)}
                      aria-label="Ta bort foto"
                      className="absolute top-1 right-1 bg-black/50 rounded-full p-1 text-white active:bg-black/70"
                    >
                      <X size={12} />
                    </button>
                  </div>
                ))}
              </div>
            )}

            {availablePhotos.length > 0 && (
              <div>
                <p className="text-[13px] text-[var(--text-tertiary)] mb-1.5">Tillgängliga foton</p>
                <div className="grid grid-cols-3 sm:grid-cols-4 gap-2">
                  {availablePhotos.map((photo) => (
                    <div
                      key={photo.id}
                      className="relative aspect-square rounded-lg overflow-hidden cursor-pointer border-2 border-transparent hover:border-accent-500 transition-colors"
                      onClick={() => handleAddPhoto(photo)}
                    >
                      <img
                        src={photo.thumbnail || photo.url}
                        loading="lazy"
                        alt=""
                        className="w-full h-full object-cover"
                        onError={() => handleImageError(photo.id)}
                      />
                      <div className="absolute top-1 right-1 bg-emerald-500 rounded-full p-0.5 text-white">
                        <Plus size={12} />
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </SectionGroup>
      </form>

    </PageShell>
  );
};

export default DamageReportFormPage;
